import { FC, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FaFacebookF, FaInstagram, FaUser, FaGlobe } from 'react-icons/fa6'
import { FaShoppingCart } from 'react-icons/fa'
import { SOCIAL_LINKS, TEA_FORMATS, TEA_PRODUCT_NAMES } from '@/constants/products'
import { LoginModal } from '@/components/features'
import { useCart } from '@/contexts/CartContext'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useAuth, useI18n } from '@/hooks'

const getCartItemName = (
  item: { name?: string; key: keyof typeof TEA_PRODUCT_NAMES; format?: string },
  lang: 'en' | 'zh'
) => {
  if (item.name) {
    return item.name
  }

  const baseName = TEA_PRODUCT_NAMES[item.key][lang]
  const format = item.format ? TEA_FORMATS[item.format as keyof typeof TEA_FORMATS]?.[lang] : ''

  if (!format || item.format === 'gift-sets') {
    return baseName
  }

  return `${baseName} ${format}`
}

const TopNav: FC = () => {
  const [showLogin, setShowLogin] = useState(false)
  const { cartItems, getTotalItems, getSubtotal } = useCart()
  const { formatProductPrice, formatAmount, currency } = useCurrency()
  const { user, isAuthenticated } = useAuth()
  const { lang, t, toggleLang } = useI18n()
  const navigate = useNavigate()
  const totalItems = getTotalItems()
  const subtotal = currency === 'TWD'
    ? cartItems.reduce((sum, item) => sum + (item.priceTwd ?? 0) * item.quantity, 0)
    : getSubtotal()

  const handleUserMenuToggle = () => {
    if (isAuthenticated) {
      navigate('/account')
    } else {
      setShowLogin(true)
    }
  }

  return (
    <>
      <header className="w-full h-14 fixed top-0 left-0 z-50 bg-white shadow-sm">
        <div className="relative w-full h-full">
          {/* 左上角 FB / IG icons - 位置互換，間距統一 */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-4">
            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              <FaFacebookF size={20} />
            </a>
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-pink-500 transition-colors"
            >
              <FaInstagram size={20} />
            </a>
          </div>

          {/* 右上角 購物車 / 登入 / 語言切換 */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-4">
            {/* 購物車按鈕 */}
            <div className="group relative flex items-center">
              <Link
                to="/cart"
                className="relative flex items-center justify-center text-gray-700 transition-colors hover:text-black"
                aria-label={t.shoppingCart}
              >
                <FaShoppingCart size={20} />
                {totalItems > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neutral-950 px-1 text-xs font-medium text-white">
                    {totalItems > 99 ? '99+' : totalItems}
                  </span>
                )}
              </Link>

              <div className="pointer-events-none absolute right-0 top-full z-50 w-[360px] translate-y-4 border border-neutral-200 bg-white px-6 py-6 text-neutral-900 opacity-0 shadow-[0_18px_45px_rgba(0,0,0,0.08)] transition duration-200 group-hover:pointer-events-auto group-hover:translate-y-3 group-hover:opacity-100">
                <div className="flex items-center justify-between border-b border-neutral-200 pb-4">
                  <h2 className="text-lg font-medium tracking-tight">{t.shoppingCart}</h2>
                  <span className="text-xs uppercase tracking-[0.18em] text-neutral-400">{totalItems} items</span>
                </div>

                {cartItems.length === 0 ? (
                  <div className="py-8 text-sm leading-6 text-neutral-500">
                    {t.noProductsInCart}
                  </div>
                ) : (
                  <div className="max-h-[360px] divide-y divide-neutral-100 overflow-auto">
                    {cartItems.slice(0, 4).map(item => {
                      const name = getCartItemName(item, lang)
                      const format = item.format ? TEA_FORMATS[item.format as keyof typeof TEA_FORMATS]?.[lang] : ''

                      return (
                        <Link
                          key={`${item.key}-${item.format}`}
                          to={`/shop/${item.id || `${item.key}-${item.format}`}`}
                          className="grid grid-cols-[64px_minmax(0,1fr)] gap-4 py-4 transition hover:bg-neutral-50"
                        >
                          <img src={item.image} alt={name} className="aspect-square w-16 object-cover" />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-neutral-950">{name}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.12em] text-neutral-400">{format}</p>
                            <p className="mt-2 text-sm text-neutral-500">
                              {item.quantity} x {formatProductPrice(item.price, item.priceTwd)}
                            </p>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                )}

                <div className="border-t border-neutral-200 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">{lang === 'zh' ? '小計' : 'Subtotal'}</span>
                    <span className="font-medium text-neutral-950">{formatAmount(subtotal)}</span>
                  </div>
                  <Link
                    to="/cart"
                    className="mt-5 flex h-12 items-center justify-center bg-neutral-950 px-5 text-xs font-medium uppercase tracking-[0.18em] text-white transition hover:bg-neutral-700"
                  >
                    {lang === 'zh' ? '查看購物車' : 'View cart'}
                  </Link>
                </div>
              </div>
            </div>

            {/* 用戶頭像 / 登入按鈕 */}
            <div className="relative group flex items-center">
              <button
                className="flex items-center justify-center text-gray-700 hover:text-black transition-colors bg-transparent border-none cursor-pointer"
                onClick={handleUserMenuToggle}
              >
                {isAuthenticated && user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || t.user}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <FaUser size={20} />
                )}
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 text-sm text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap px-2 py-1 bg-white shadow rounded pointer-events-none z-10">
                {isAuthenticated ? (user?.name || t.logout) : t.login}
              </div>
            </div>

            {/* 語言切換按鈕（中 / EN） */}
            <button
              className="flex items-center justify-center text-gray-700 hover:text-black transition-colors bg-transparent border-none cursor-pointer gap-1 min-w-[32px]"
              onClick={toggleLang}
              title={t.switchLanguage}
            >
              <FaGlobe size={20} />
              <span className="text-xs w-4 text-center inline-block">
                {t.language}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* 登入 Modal */}
      {showLogin && (
        <LoginModal
          lang={lang}
          onClose={() => setShowLogin(false)}
        />
      )}
    </>
  )
}

export default TopNav
